<template>
    <div class="bg-white rounded shadow-sm mb-4">
        <div class="d-flex flex-row flex-wrap align-items-center justify-content-between mb-2" style="padding: 20px 20px 10px 20px">
            <h3 class="h5 font-weight-semibold mb-0" style="color: #2F2F2F;">QR Distribution Summary</h3>
            <div class="d-flex flex-row align-items-center flex-wrap">
                <!-- Search Input -->
                <div class="search-input-wrapper mr-2 my-1">
                    <input
                        type="text"
                        v-model="searchDealer"
                        placeholder="Cari Dealer"
                        class="search-input"
                    />
                    <i class="fas fa-search search-icon"></i>
                </div>

                <!-- Date Range Picker -->
                <div class="date-picker-wrapper mr-2 my-1">
                    <i class="far fa-calendar-alt calendar-icon"></i>
                    <flat-pickr
                        ref="datepicker"
                        v-model="selected.date"
                        :config="dateRangeConfig"
                        class="summary-flatpickr-input"
                    ></flat-pickr>
                </div>

                <!-- Dropdown Dealer -->
                <div style="width: 170px; margin: 4px 8px 4px 0;">
                    <MultiSelect
                        v-model="filter.dealer"
                        :options="options.dealer"
                        class="qtrust-select"
                        placeholder="Semua Dealer"
                    ></MultiSelect>
                </div>

                <!-- Download CSV -->
                <button
                    type="button"
                    class="btn btn-download-csv my-1"
                    @click="exportToCSV"
                >
                    Download CSV <i class="fas fa-file-download ml-2"></i>
                </button>
            </div>
        </div>

        <!-- Summary Stats -->
        <div class="d-flex align-items-center px-4 py-3" style="border-top: 1px solid #F3F3F3;">
            <div class="mr-5">
                <div style="font-size: 13px; color: #4B5563; margin-bottom: 2px;">Total Pengiriman Dari MD</div>
                <div style="font-size: 20px; font-weight: 700; color: #111827;">{{ numberWithCommas(totalPengirimanMD) }}</div>
            </div>
            <div>
                <div style="font-size: 13px; color: #4B5563; margin-bottom: 2px;">Total Pemakaian Dari Dealer</div>
                <div style="font-size: 20px; font-weight: 700; color: #111827;">{{ numberWithCommas(totalPemakaianDealer) }}</div>
            </div>
        </div>

        <!-- Table -->
        <div class="w-100 overflow-auto">
            <table class="table w-100 qtrust-table">
                <thead>
                    <tr>
                        <th style="width: 28%;">Nama Dealer</th>
                        <th style="width: 24%;">Pengiriman Dari MD</th>
                        <th style="width: 24%;">Pemakaian Dari Dealer</th>
                        <th style="width: 24%;">Pengiriman Dari Dealer ke Pos</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, idx) in paginatedData" :key="idx">
                        <td>{{ item.name }}</td>
                        <td>{{ numberWithCommas(item.sent_md) }}</td>
                        <td>{{ numberWithCommas(item.used_dealer) }}</td>
                        <td>{{ numberWithCommas(item.sent_pos) }}</td>
                    </tr>
                    <tr v-if="paginatedData.length === 0">
                        <td colspan="4" class="text-center text-muted py-3">Tidak ada data ditemukan</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-end align-items-center py-2 px-3">
            <p class="text-muted mb-0" style="font-size: 13px;">
                Showing {{ filteredList.length ? (config.page - 1) * config.limit + 1 : 0 }}-{{ Math.min(config.page * config.limit, filteredList.length) }} of {{ filteredList.length }} Items
            </p>
            <div class="d-flex align-items-center text-muted ml-3">
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(1)">
                    <i class="fa fa-angle-double-left"></i>
                </button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page <= 1" @click="goToPage(config.page - 1)">
                    <i class="fa fa-angle-left"></i>
                </button>
                <p class="px-2 text-dark mb-0" style="font-size: 13px;">{{ config.page }}</p>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= lastPageCount" @click="goToPage(config.page + 1)">
                    <i class="fa fa-angle-right"></i>
                </button>
                <button class="btn btn-link px-1" style="color: #627884" :disabled="config.page >= lastPageCount" @click="goToPage(lastPageCount)">
                    <i class="fa fa-angle-double-right"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import * as XLSX from 'xlsx';

export default {
    data() {
        // Generate 50 dealers with exact values for Dealer 1 - 10 matching design
        const initialDealers = [
            { name: 'Dealer 1', sent_md: 150, used_dealer: 50, sent_pos: 30 },
            { name: 'Dealer 2', sent_md: 200, used_dealer: 120, sent_pos: 20 },
            { name: 'Dealer 3', sent_md: 100, used_dealer: 30, sent_pos: 10 },
            { name: 'Dealer 4', sent_md: 100, used_dealer: 50, sent_pos: 10 },
            { name: 'Dealer 5', sent_md: 100, used_dealer: 70, sent_pos: 10 },
            { name: 'Dealer 6', sent_md: 100, used_dealer: 70, sent_pos: 10 },
            { name: 'Dealer 7', sent_md: 100, used_dealer: 70, sent_pos: 10 },
            { name: 'Dealer 8', sent_md: 100, used_dealer: 70, sent_pos: 10 },
            { name: 'Dealer 9', sent_md: 100, used_dealer: 70, sent_pos: 10 },
            { name: 'Dealer 10', sent_md: 100, used_dealer: 70, sent_pos: 10 },
        ];

        for (let i = 11; i <= 50; i++) {
            initialDealers.push({
                name: `Dealer ${i}`,
                sent_md: 100,
                used_dealer: 60,
                sent_pos: 10
            });
        }

        return {
            moment: moment,
            searchDealer: '',
            selected: {
                date: '03-10-2024 to 04-10-2024',
            },
            dateRangeConfig: {
                mode: "range",
                altFormat: "d-m-Y",
                dateFormat: "d-m-Y",
                altInput: true,
                altInputClass: "summary-flatpickr-alt",
                locale: {
                    rangeSeparator: " s/d "
                }
            },
            filter: {
                dealer: null,
            },
            options: {
                dealer: [
                    'Semua Dealer',
                    ...Array.from({ length: 50 }, (_, i) => `Dealer ${i + 1}`)
                ],
            },
            dealersList: initialDealers,
            config: {
                page: 1,
                limit: 10,
            },
            totalPengirimanMD: 650,
            totalPemakaianDealer: 320,
        };
    },
    computed: {
        filteredList() {
            let list = this.dealersList;
            if (this.searchDealer && this.searchDealer.trim() !== '') {
                const q = this.searchDealer.toLowerCase().trim();
                list = list.filter(item => item.name.toLowerCase().includes(q));
            }
            if (this.filter.dealer && this.filter.dealer !== 'Semua Dealer') {
                list = list.filter(item => item.name === this.filter.dealer);
            }
            return list;
        },
        lastPageCount() {
            return Math.ceil(this.filteredList.length / this.config.limit) || 1;
        },
        paginatedData() {
            const start = (this.config.page - 1) * this.config.limit;
            return this.filteredList.slice(start, start + this.config.limit);
        }
    },
    watch: {
        searchDealer() {
            this.config.page = 1;
        },
        'filter.dealer'() {
            this.config.page = 1;
        }
    },
    methods: {
        goToPage(page) {
            if (page >= 1 && page <= this.lastPageCount) {
                this.config.page = page;
            }
        },
        exportToCSV() {
            const dataToExport = this.filteredList.map(item => ({
                'Nama Dealer': item.name,
                'Pengiriman Dari MD': item.sent_md,
                'Pemakaian Dari Dealer': item.used_dealer,
                'Pengiriman Dari Dealer ke Pos': item.sent_pos,
            }));

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'QR Distribution Summary');

            const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
            XLSX.writeFile(workbook, `qr_distribution_summary_${timestamp}.csv`, { bookType: 'csv' });
        }
    }
};
</script>

<style scoped>
.search-input-wrapper {
    display: flex;
    align-items: center;
    background-color: white;
    border: 1px solid #E2E8F0;
    border-radius: 4px;
    padding: 0 10px;
    height: 32px;
    width: 150px;
}

.search-input {
    border: none;
    outline: none;
    font-size: 13px;
    width: 100%;
    background: transparent;
    color: #1F2937;
}

.search-input::placeholder {
    color: #4B5563;
}

.search-icon {
    color: #4B5563;
    font-size: 12px;
    margin-left: 4px;
}

.date-picker-wrapper {
    display: flex;
    align-items: center;
    background-color: white;
    border: 1px solid #E2E8F0;
    border-radius: 4px;
    padding: 0 10px;
    height: 32px;
}

.calendar-icon {
    color: #4B5563;
    font-size: 14px;
    margin-right: 6px;
}

.btn-download-csv {
    background-color: white;
    border: 1px solid #E2E8F0;
    padding: 4.5px 14px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    font-size: 13px;
    cursor: pointer;
    font-weight: 500;
    color: #1F2937;
    height: 32px;
}

.btn-download-csv:hover {
    background-color: #F8FAFC;
}

.qtrust-table > thead > tr > th {
    text-align: left;
    border-top: 2px solid #F3F3F3;
    border-bottom: 2px solid #F3F3F3;
    padding: 12px 20px;
    font-size: 13px;
    font-weight: 600;
    color: #2F2F2F;
}

.qtrust-table > tbody > tr > td {
    text-align: left;
    padding: 12px 20px;
    border-bottom: 1px solid #F3F3F3;
    font-size: 13px;
    color: #374151;
}
</style>

<style>
.summary-flatpickr-alt {
    border: none !important;
    outline: none !important;
    font-size: 13px !important;
    background: transparent !important;
    color: #1F2937 !important;
    width: 200px !important;
    height: 30px !important;
    padding: 0 !important;
    cursor: pointer !important;
}
</style>
